import { alpha } from '@mui/material/styles';
import { createTheme, ThemeOptions, Theme } from '@mui/material/styles';
import { ptBR } from '@mui/x-date-pickers/locales';

export const esquemasCores = {
  slate: {
    primary: '#0F172A',   // Slate 900
    secondary: '#64748B', // Slate 500
    success: '#10B981',   // Emerald 500
    background: '#F8FAFC' // Slate 50
  },
  ocean: {
    primary: '#075985',   // Sky 900
    secondary: '#0EA5E9', // Sky 500
    success: '#10B981',
    background: '#F0F9FF'
  },
  midnight: {
    primary: '#1E1B4B',   // Indigo 950
    secondary: '#4338CA', // Indigo 700
    success: '#059669',
    background: '#F5F3FF'
  },
  forest: {
    primary: '#064E3B',   // Emerald 950
    secondary: '#059669', // Emerald 600
    success: '#10B981',
    background: '#ECFDF5'
  },
  zinc: {
    primary: '#18181B',   // Zinc 900
    secondary: '#71717A', // Zinc 500
    success: '#10B981',
    background: '#FAFAFA'
  },
  // --- NOVOS TEMAS ---
  medical: {
    primary: '#0891B2',   // Cyan 600 (Clínicas, ideal para saúde)
    secondary: '#67E8F9', // Cyan 300
    success: '#22C55E',
    background: '#F0FDFA' // Mint White
  },
  harvest: {
    primary: '#92400E',   // Amber 800 (Casa de Ração / Agro / Pet)
    secondary: '#D97706', // Amber 600
    success: '#84CC16',
    background: '#FFFBEB' // Cream
  },
  rose: {
    primary: '#881337',   // Rose 900 (Estética / Bem-estar)
    secondary: '#E11D48', // Rose 600
    success: '#10B981',
    background: '#FFF1F2'
  },
  coffee: {
    primary: '#451A03',   // Brown 950 (Escritórios / Elegância)
    secondary: '#92400E', // Brown 800
    success: '#059669',
    background: '#FAFAF9'
  },
  violet: {
    primary: '#4C1D95',   // Violet 900
    secondary: '#8B5CF6', // Violet 500
    success: '#10B981',
    background: '#F5F3FF'
  }
};

export type NomeTema = keyof typeof esquemasCores;

/**
 * FUNÇÃO GERADORA DE TEMA
 * @param tipo Nome do esquema de cores escolhido
 */
export const criarTemaCustomizado = (tipo: NomeTema = 'slate') => {
  const cores = esquemasCores[tipo];

  const themeOptions: ThemeOptions = {
    palette: {
      mode: 'light',
      primary: { 
        main: cores.primary, 
        contrastText: '#ffffff' 
      },
      secondary: { 
        main: cores.secondary, 
        contrastText: '#ffffff' 
      },
      success: {
        main: cores.success,
        contrastText: '#ffffff'
      },
      error: {
        main: '#EF4444',
      },
      warning: {
        main: '#F59E0B',
      },
      background: { 
        default: cores.background, 
        paper: '#ffffff' 
      },
      text: { 
        primary: cores.primary, 
        secondary: '#475569' 
      },
      divider: '#E2E8F0',
    },

    typography: {
      fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
      htmlFontSize: 16,
      body1: { fontSize: '0.90rem', lineHeight: 1.5 },
      body2: { fontSize: '0.84rem', lineHeight: 1.43 },
      button: {
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none', 
      },
      h6: {
        fontWeight: 700,
        fontSize: '1.1rem',
      }
    },

    shape: { borderRadius: 10 },

    components: {
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: 42, 
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#CBD5E1', 
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px',
            },
          },
          input: {
            padding: '10px 14px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            transform: 'translate(14px, 11px) scale(1)',
            color: '#64748B',
          },
          shrink: {
            transform: 'translate(14px, -8px) scale(0.85)',
            fontWeight: 600,
            background: '#ffffff',
            padding: '0 6px',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            height: 42, 
            padding: '8px 20px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            backgroundColor: cores.primary,
            '&:hover': {
              backgroundColor: `${cores.primary}e6`, 
            },
          },
          outlined: {
            borderColor: '#E2E8F0',
            color: '#475569',
            '&:hover': {
              borderColor: '#CBD5E1',
              backgroundColor: cores.background,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            border: '1px solid #E2E8F0',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            padding: 8,
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '0 8px',
            padding: '10px 12px',
          },
        },
      },
    },
  };

  return createTheme(themeOptions, ptBR);
};

const tema = criarTemaCustomizado('slate');
export default tema;

// ─── Padrão de botão toggle ───────────────────────────────────────────────────

/**
 * Padrão de `sx` para IconButton no estado "ativo/ligado".
 * Filled com a cor primary do tema + glow suave — visualmente "aceso".
 * Respeita qualquer esquema de cores selecionado pelo usuário.
 *
 * @example
 * <IconButton sx={{ borderRadius:'8px', border:'1px solid',
 *   ...(ativo ? sxBotaoAtivo(theme) : sxBotaoInativo) }}>
 */
export const sxBotaoAtivo = (theme: Theme) => ({
  borderColor: theme.palette.primary.main,
  bgcolor:     theme.palette.primary.main,
  color:       theme.palette.primary.contrastText,
  boxShadow:   `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
  '&:hover': {
    bgcolor:   alpha(theme.palette.primary.main, 0.88),
    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.45)}`,
  },
});

/** Estado "inativo/desligado" — complemento de sxBotaoAtivo. */
export const sxBotaoInativo = {
  borderColor: 'divider',
  bgcolor:     'background.paper',
  color:       'text.secondary',
} as const;